import React, { useEffect, useState } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import Get from '../../api/internal/Get';
import Button from '../../assets/essentials/Button';
import Input from '../../assets/essentials/Input';
import Select from '../../assets/essentials/Select';
import BBITable from '../../helpers/bBITable/BBITable';
import FilterData from '../../helpers/filter/FilterData';
import OptionList from '../../helpers/options/OptionList';
import View from '../../helpers/slab/View';
import ViewTruck from '../../features/views/assets/truck/Truck';
import classesCustom from '../../helpers/search/CustomSearch.module.css';
import { PageLoadingSpinner } from '../../helpers/loadingSpinner/LoadingSpinner';

function SearchTruck() {
  const [trucks, setTrucks] = useState<TruckBoard[]>([]);
  const [searchTruckParams, setSearchTruckParams] = useSearchParams();
  const [truckOptions, setTruckOptions] = useState<CreateTruckOptions | null>();
  const [isLoading, setIsLoading] = useState(true);

  const filterValue = useOutletContext();

  useEffect(() => {
    if (truckOptions) {
      return;
    }
    Get(`/Asset/GetCreateTruck`).then(response => {
      if (response) {
        setTruckOptions(response.data);
      }
    });
  }, [truckOptions]);

  useEffect(() => {
    setIsLoading(true);
    Get(`/Asset/SearchTrucks?${searchTruckParams.toString()}`)
      .then(response => {
        if (response) {
          setTrucks(response.data);
          setIsLoading(false);
        }
      })
      .finally(() => setIsLoading(false));
  }, [searchTruckParams]);

  const handleFormSubmit = event => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const newParams = new URLSearchParams();
    formData.forEach((value, key) => {
      if (value !== '') {
        newParams.set(key, value.toString());
      }
    });
    setSearchTruckParams(newParams, { replace: true });
  };

  const columnConfig = [
    {
      key: '1',
      attribute: 'truckName',
      attributeprimaryhidden: 'assetId',
      header: 'Truck Unit #',
      onClick: e =>
        View(
          <ViewTruck
            truckName={e.target.innerText}
            assetId={e.target.dataset.attributeprimaryhidden}
          />,
        ),
    },
    {
      key: '2',
      attribute: 'vin',
      header: 'VIN',
    },
    {
      key: '3',
      attribute: 'make',
      header: 'Make',
    },
    {
      key: '4',
      attribute: 'model',
      header: 'Model',
    },
    {
      key: '5',
      attribute: 'year',
      header: 'Year',
    },
    {
      key: '6',
      attribute: 'licensePlateNumber',
      header: 'Plate',
    },
    {
      key: '7',
      attribute: 'licensePlateState',
      header: 'Plate State',
    },
    {
      key: '8',
      attribute: 'ownership',
      header: 'Ownership',
    },
    {
      key: '9',
      attribute: 'truckType',
      header: 'Type',
    },
    {
      key: '10',
      attribute: 'driverName',
      header: 'Driver',
    },
    {
      key: '10',
      attribute: 'weeklyCost',
      header: 'Weekly Cost',
      dataType: 'currency',
    },
  ];

  const tableData = FilterData(trucks, filterValue);

  return (
    <>
      <div className={`${classesCustom.customSearch}`}>
        <form onSubmit={handleFormSubmit}>
          {/* Truck Unit # */}
          <label htmlFor="truckName">
            Truck Unit #
            <Input
              type="text"
              name="truckName"
              id="truckName"
              defaultValue={searchTruckParams.get('truckName') ?? ''}
            />
          </label>
          {/* VIN */}
          <label htmlFor="vin">
            VIN
            <Input
              type="text"
              name="vin"
              id="vin"
              defaultValue={searchTruckParams.get('vin') ?? ''}
            />
          </label>
          {/* Make */}
          <label htmlFor="make">
            Make
            <Input
              type="text"
              name="make"
              id="make"
              defaultValue={searchTruckParams.get('make') ?? ''}
            />
          </label>
          {/* Model */}
          <label htmlFor="model">
            Model
            <Input
              type="text"
              name="model"
              id="model"
              defaultValue={searchTruckParams.get('model') ?? ''}
            />
          </label>
          {/* Year */}
          <label htmlFor="year">
            Year
            <Input
              type="text"
              name="year"
              id="year"
              defaultValue={searchTruckParams.get('year') ?? ''}
            />
          </label>
          {/* Plate */}
          <label htmlFor="licensePlateNumber">
            Plate
            <Input
              type="text"
              name="licensePlateNumber"
              id="licensePlateNumber"
              defaultValue={searchTruckParams.get('licensePlateNumber') ?? ''}
            />
          </label>
          {/* Ownership */}
          <label htmlFor="ownershipTypeId">
            Ownership
            <Select
              id="ownershipTypeId"
              name="ownershipTypeId"
              defaultValue={searchTruckParams.get('ownershipTypeId') ?? ''}
            >
              <option value="">-- Any --</option>
              <OptionList
                optionData={truckOptions?.ownershipTypeOptions}
                attributeID="ownershipTypeId"
                attributeName="name"
              />
            </Select>
          </label>
          {/* Type */}
          <label>
            Type
            <Select
              id="truckTypeId"
              name="truckTypeId"
              defaultValue={searchTruckParams.get('truckTypeId') ?? ''}
            >
              <option value="">-- Any --</option>
              <OptionList
                optionData={truckOptions?.truckTypeOptions}
                attributeID="truckTypeId"
                attributeName="type"
                attributeGroup="group"
              />
            </Select>
          </label>
          {/* Asset Status */}
          <label htmlFor="isActive">
            Asset Status
            <Select
              name="isActive"
              id="isActive"
              defaultValue={searchTruckParams.get('isActive') ?? ''}
            >
              <option value="">-- Any --</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </label>
          <div className={classesCustom.submitHolder}>
            <Button type="submit" variant="good">
              Search
            </Button>
            <Button
              onClick={() => setSearchTruckParams({})}
              type="reset"
              variant="bad"
            >
              Clear
            </Button>
          </div>
        </form>
      </div>
      {isLoading ? (
        <PageLoadingSpinner />
      ) : (
        <BBITable data={tableData} columns={columnConfig} />
      )}
    </>
  );
}

export default SearchTruck;
